import React from 'react';
import styled from 'styled-components';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';

const PaginationInfo = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.tertiary};
  font-weight: ${typography.fontWeight.medium};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  text-align: center;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const PageInfo = styled.div`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};
`;

const ItemInfo = styled.div`
  font-size: 13px;
  color: ${colors.semantic.text.disabled};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 768px) {
    order: 1;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PageButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid
    ${props =>
      props.active ? colors.primary[500] : colors.neutral[300]};
  background: ${props =>
    props.active ? colors.primary[500] : colors.semantic.background.primary};
  color: ${props =>
    props.active
      ? colors.semantic.text.inverse
      : colors.semantic.text.secondary};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props =>
    props.active
      ? typography.fontWeight.semibold
      : typography.fontWeight.medium};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  min-width: 36px;
  min-height: 36px;

  &:hover:not(:disabled) {
    background: ${props =>
      props.active ? colors.primary[600] : colors.secondary[50]};
    border-color: ${props =>
      props.active ? colors.primary[600] : colors.secondary[400]};
    transform: translateY(-1px);
    box-shadow: ${shadows.sm};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${shadows.sm};
  }

  &:disabled {
    opacity: 0.4;
    background: ${colors.secondary[50]};
    color: ${colors.semantic.text.disabled};
    border-color: ${colors.semantic.border.primary};
    transform: none;
    box-shadow: none;
  }
`;

const NavButton = styled(PageButton)`
  padding: 10px ${spacing[4]};
  font-weight: ${typography.fontWeight.semibold};
  min-width: 80px;

  &:not(:disabled):hover {
    background: ${colors.primary[500]};
    color: ${colors.semantic.text.inverse};
    border-color: ${colors.primary[500]};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[6]} 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${spacing[4]};
  }
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  total,
  limit,
  onPageChange,
}) => {
  const getVisiblePages = () => {
    const maxVisible = 5;
    const pages = [];

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const visiblePages = getVisiblePages();

  return (
    <PaginationContainer>
      <PaginationInfo>
        <PageInfo>
          {currentPage} / {totalPages} 페이지
        </PageInfo>
        <ItemInfo>
          {total > 0 ? `${startItem}-${endItem}` : '0'} / {total}개 항목
        </ItemInfo>
      </PaginationInfo>

      <ButtonContainer>
        <NavButton
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          ‹ 이전
        </NavButton>

        {visiblePages.map((pageNumber) => (
          <PageButton
            key={pageNumber}
            active={pageNumber === currentPage}
            onClick={() => handlePageChange(pageNumber)}
          >
            {pageNumber}
          </PageButton>
        ))}

        <NavButton
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          다음 ›
        </NavButton>
      </ButtonContainer>
    </PaginationContainer>
  );
};

export default Pagination;
